// Dictionary for the 997 (Functional Acknowledgment) — AK1/AK2/AK3/AK4/AK5/AK9.
// ST and SE are shared segments already defined in header-segments.ts and are
// not redefined here. Generic X12 997 (005010 structural basis) definitions
// only; the AK-segment status/error code lists are transcribed from the
// reference document "997 - Functional Acknowledgment Qualifiers.txt"
// (labeled X12 004010 — these codes are stable across versions, per the
// approved requirements).

import {LoopId, type ElementDefinition, type SegmentDefinition} from './types';
import {FUNCTIONAL_IDENTIFIER_CODES} from './functional-identifier-code';
import type {CodeListEntry} from './types';

function el(
  position: number,
  label: string,
  description: string,
  extra: Partial<ElementDefinition> = {}
): ElementDefinition {
  return {position, label, description, ...extra};
}

const SEGMENT_SYNTAX_ERROR_CODES: CodeListEntry[] = [
  {code: '1', label: 'Unrecognized segment ID'},
  {code: '2', label: 'Unexpected segment'},
  {code: '3', label: 'Mandatory segment missing'},
  {code: '4', label: 'Loop Occurs Over Maximum Times'},
  {code: '5', label: 'Segment Exceeds Maximum Use'},
  {code: '6', label: 'Segment Not in Defined Transaction Set'},
  {code: '7', label: 'Segment Not in Proper Sequence'},
  {code: '8', label: 'Segment Has Data Element Errors'},
];

const DATA_ELEMENT_SYNTAX_ERROR_CODES: CodeListEntry[] = [
  {code: '1', label: 'Mandatory data element missing'},
  {code: '2', label: 'Conditional required data element missing'},
  {code: '3', label: 'Too many data elements'},
  {code: '4', label: 'Data element too short'},
  {code: '5', label: 'Data element too long'},
  {code: '6', label: 'Invalid character in data element'},
  {code: '7', label: 'Invalid code value'},
  {code: '8', label: 'Invalid Date'},
  {code: '9', label: 'Invalid Time'},
  {code: '10', label: 'Exclusion Condition Violated'},
];

const TRANSACTION_SET_ACKNOWLEDGMENT_CODES: CodeListEntry[] = [
  {code: 'A', label: 'Accepted'},
  {code: 'E', label: 'Accepted But Errors Were Noted'},
  {code: 'M', label: 'Rejected, Message Authentication Code (MAC) Failed'},
  {code: 'R', label: 'Rejected'},
  {code: 'W', label: 'Rejected, Assurance Failed Validity Tests'},
  {
    code: 'X',
    label: 'Rejected, Content After Decryption Could Not Be Analyzed',
  },
];

const TRANSACTION_SET_SYNTAX_ERROR_CODES: CodeListEntry[] = [
  {code: '1', label: 'Transaction Set Not Supported'},
  {code: '2', label: 'Transaction Set Trailer Missing'},
  {
    code: '3',
    label: 'Transaction Set Control Number in Header and Trailer Do Not Match',
  },
  {
    code: '4',
    label: 'Number of Included Segments Does Not Match Actual Count',
  },
  {code: '5', label: 'One or More Segments in Error'},
  {code: '6', label: 'Missing or Invalid Transaction Set Identifier'},
  {code: '7', label: 'Missing or Invalid Transaction Set Control Number'},
  {
    code: '23',
    label:
      'Transaction Set Control Number Not Unique within the Functional Group',
  },
];

const FUNCTIONAL_GROUP_ACKNOWLEDGE_CODES: CodeListEntry[] = [
  {code: 'A', label: 'Accepted'},
  {code: 'E', label: 'Accepted, But Errors Were Noted'},
  {code: 'M', label: 'Rejected, Message Authentication Code (MAC) Failed'},
  {
    code: 'P',
    label: 'Partially Accepted, At Least One Transaction Set Was Rejected',
  },
  {code: 'R', label: 'Rejected'},
  {code: 'W', label: 'Rejected, Assurance Failed Validity Tests'},
  {
    code: 'X',
    label: 'Rejected, Content After Decryption Could Not Be Analyzed',
  },
];

const FUNCTIONAL_GROUP_SYNTAX_ERROR_CODES: CodeListEntry[] = [
  {code: '1', label: 'Functional Group Not Supported'},
  {code: '2', label: 'Functional Group Version Not Supported'},
  {code: '3', label: 'Functional Group Trailer Missing'},
  {
    code: '4',
    label:
      'Group Control Number in the Functional Group Header and Trailer Do Not Agree',
  },
  {
    code: '5',
    label: 'Number of Included Transaction Sets Does Not Match Actual Count',
  },
  {code: '6', label: 'Group Control Number Violates Syntax'},
];

export const SEGMENTS_997: Record<string, SegmentDefinition> = {
  AK1: {
    id: 'AK1',
    name: 'Functional Group Response Header',
    purpose:
      'Identifies the functional group being acknowledged — its type and group control number. Occurs once, right after ST.',
    loop: LoopId.FunctionalGroupResponse,
    required: true,
    elements: [
      el(
        1,
        'Functional Identifier Code',
        'The type of transaction sets in the functional group being acknowledged.',
        {
          dataType: 'code',
          required: true,
          codes: FUNCTIONAL_IDENTIFIER_CODES,
        }
      ),
      el(
        2,
        'Group Control Number',
        "The acknowledged functional group's own GS06/GE02 control number.",
        {dataType: 'numeric', required: true}
      ),
    ],
  },

  AK2: {
    id: 'AK2',
    name: 'Transaction Set Response Header',
    purpose:
      'Opens one acknowledged transaction set within the group — its type and control number. One AK2 loop per transaction set in the acknowledged group.',
    loop: LoopId.TransactionSetResponse,
    elements: [
      el(
        1,
        'Transaction Set Identifier Code',
        "The acknowledged transaction's ST01 value, e.g. 850.",
        {dataType: 'identifier', required: true}
      ),
      el(
        2,
        'Transaction Set Control Number',
        "The acknowledged transaction's own ST02/SE02 control number.",
        {required: true}
      ),
    ],
  },

  AK3: {
    id: 'AK3',
    name: 'Data Segment Note',
    purpose:
      'Reports one segment-level error found in the acknowledged transaction set.',
    loop: LoopId.SegmentNote,
    elements: [
      el(1, 'Segment ID Code', 'The id of the segment in error, e.g. PO1.', {
        dataType: 'identifier',
        required: true,
      }),
      el(
        2,
        'Segment Position in Transaction Set',
        'The 1-based position of the segment in error, counting from ST.',
        {dataType: 'numeric', required: true}
      ),
      el(
        3,
        'Loop Identifier Code',
        'The loop the segment in error belongs to, when it is inside one.'
      ),
      el(4, 'Segment Syntax Error Code', 'What was wrong with the segment.', {
        dataType: 'code',
        codes: SEGMENT_SYNTAX_ERROR_CODES,
      }),
    ],
  },

  AK4: {
    id: 'AK4',
    name: 'Data Element Note',
    purpose:
      'Reports one element-level error within a segment already reported by an AK3.',
    loop: LoopId.ElementNote,
    elements: [
      el(
        1,
        'Element Position in Segment',
        'The 1-based position of the data element in error within its segment.',
        {dataType: 'numeric', required: true}
      ),
      el(
        2,
        'Data Element Reference Number',
        "The X12 data element's reference number, when known.",
        {dataType: 'numeric'}
      ),
      el(
        3,
        'Data Element Syntax Error Code',
        'What was wrong with the data element.',
        {
          dataType: 'code',
          required: true,
          codes: DATA_ELEMENT_SYNTAX_ERROR_CODES,
        }
      ),
      el(
        4,
        'Copy of Bad Data Element',
        'The offending value itself, copied from the acknowledged transaction set.'
      ),
    ],
  },

  AK5: {
    id: 'AK5',
    name: 'Transaction Set Response Trailer',
    purpose:
      'Closes an AK2 loop: whether the acknowledged transaction set was accepted, and up to five syntax error codes if not.',
    loop: LoopId.TransactionSetResponse,
    required: true,
    elements: [
      el(
        1,
        'Transaction Set Acknowledgment Code',
        'Whether the acknowledged transaction set was accepted.',
        {
          dataType: 'code',
          required: true,
          codes: TRANSACTION_SET_ACKNOWLEDGMENT_CODES,
        }
      ),
      el(
        2,
        'Transaction Set Syntax Error Code',
        'A reason the transaction set was rejected or flagged, if any.',
        {
          dataType: 'code',
          codes: TRANSACTION_SET_SYNTAX_ERROR_CODES,
        }
      ),
      el(
        3,
        'Transaction Set Syntax Error Code',
        'A reason the transaction set was rejected or flagged, if any.',
        {
          dataType: 'code',
          codes: TRANSACTION_SET_SYNTAX_ERROR_CODES,
        }
      ),
      el(
        4,
        'Transaction Set Syntax Error Code',
        'A reason the transaction set was rejected or flagged, if any.',
        {
          dataType: 'code',
          codes: TRANSACTION_SET_SYNTAX_ERROR_CODES,
        }
      ),
      el(
        5,
        'Transaction Set Syntax Error Code',
        'A reason the transaction set was rejected or flagged, if any.',
        {
          dataType: 'code',
          codes: TRANSACTION_SET_SYNTAX_ERROR_CODES,
        }
      ),
      el(
        6,
        'Transaction Set Syntax Error Code',
        'A reason the transaction set was rejected or flagged, if any.',
        {
          dataType: 'code',
          codes: TRANSACTION_SET_SYNTAX_ERROR_CODES,
        }
      ),
    ],
  },

  AK9: {
    id: 'AK9',
    name: 'Functional Group Response Trailer',
    purpose:
      'Closes the acknowledgment: whether the functional group was accepted, how many transaction sets it should have, actually had, and were accepted, and up to five syntax error codes if not accepted.',
    loop: LoopId.FunctionalGroupResponse,
    required: true,
    elements: [
      el(
        1,
        'Functional Group Acknowledge Code',
        'Whether the acknowledged functional group was accepted.',
        {
          dataType: 'code',
          required: true,
          codes: FUNCTIONAL_GROUP_ACKNOWLEDGE_CODES,
        }
      ),
      el(
        2,
        'Number of Transaction Sets Included',
        "The acknowledged group's own GS/GE count of transaction sets.",
        {dataType: 'numeric', required: true}
      ),
      el(
        3,
        'Number of Received Transaction Sets',
        'How many transaction sets this acknowledgment actually received.',
        {dataType: 'numeric', required: true}
      ),
      el(
        4,
        'Number of Accepted Transaction Sets',
        'How many of the received transaction sets were accepted.',
        {dataType: 'numeric', required: true}
      ),
      el(
        5,
        'Functional Group Syntax Error Code',
        'A reason the functional group was rejected or flagged, if any.',
        {
          dataType: 'code',
          codes: FUNCTIONAL_GROUP_SYNTAX_ERROR_CODES,
        }
      ),
      el(
        6,
        'Functional Group Syntax Error Code',
        'A reason the functional group was rejected or flagged, if any.',
        {
          dataType: 'code',
          codes: FUNCTIONAL_GROUP_SYNTAX_ERROR_CODES,
        }
      ),
      el(
        7,
        'Functional Group Syntax Error Code',
        'A reason the functional group was rejected or flagged, if any.',
        {
          dataType: 'code',
          codes: FUNCTIONAL_GROUP_SYNTAX_ERROR_CODES,
        }
      ),
      el(
        8,
        'Functional Group Syntax Error Code',
        'A reason the functional group was rejected or flagged, if any.',
        {
          dataType: 'code',
          codes: FUNCTIONAL_GROUP_SYNTAX_ERROR_CODES,
        }
      ),
      el(
        9,
        'Functional Group Syntax Error Code',
        'A reason the functional group was rejected or flagged, if any.',
        {
          dataType: 'code',
          codes: FUNCTIONAL_GROUP_SYNTAX_ERROR_CODES,
        }
      ),
    ],
  },
};
